import { useState, useRef, useEffect, useCallback } from "react";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SparkleIcon as Sparkle, X, PaperPlaneRightIcon as PaperPlaneRight, ArrowClockwiseIcon as ArrowClockwise } from "@phosphor-icons/react";

const SUGGESTS = [
  "Riassumi questo intervento tecnico: …",
  "Scrivi una bozza di risposta al ticket del cliente su …",
  "Spiega in breve un requisito NIS2",
];

export function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages] = useState([]); // {role, content}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const newChat = useCallback(async () => {
    const sid = sessionId;
    setMessages([]); setSessionId(""); setInput("");
    if (sid) { try { await fetch(`${API}/assistant/reset`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` }, body: JSON.stringify({ session_id: sid, message: "" }) }); } catch { /* noop */ } }
  }, [sessionId]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("stream error");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          let data;
          try { data = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (data.session_id) setSessionId((s) => s || data.session_id);
          if (data.delta) setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + data.delta }; return c; });
          if (data.error) { toast.error("Errore assistente: " + data.error); }
        }
      }
    } catch {
      toast.error("Impossibile contattare l'assistente");
      setMessages((m) => { const c = [...m]; if (c.length && c[c.length - 1].role === "assistant" && !c[c.length - 1].content) c.pop(); return c; });
    } finally { setBusy(false); }
  }, [input, busy, sessionId]);

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="assistant-fab"
          title="TecSuiteAI Agent"
          className="fixed bottom-24 right-5 z-50 h-14 w-14 rounded-full shadow-lg shadow-primary/30 overflow-hidden ring-2 ring-white hover:scale-105 active:scale-95 transition-transform"
        >
          <img src="/brand/chatbot-logo.png" alt="TecSuiteAI Agent" className="h-full w-full object-cover" />
        </button>
      )}

      {open && (
        <div
          data-testid="assistant-panel"
          className="fixed bottom-24 right-5 z-50 w-[calc(100vw-2.5rem)] sm:w-[400px] h-[70vh] max-h-[600px] bg-white border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-primary text-primary-foreground">
            <div className="flex items-center gap-2">
              <img src="/brand/chatbot-logo.png" alt="TecSuiteAI Agent" className="h-7 w-7 rounded-md object-cover ring-1 ring-white/30" />
              <span className="font-heading text-sm font-semibold">TecSuiteAI Agent</span>
              <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded-full">gpt-5</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={newChat} title="Nuova conversazione" data-testid="assistant-new-chat" className="h-8 w-8 flex items-center justify-center rounded-sm hover:bg-white/15 transition-colors"><ArrowClockwise size={16} /></button>
              <button onClick={() => setOpen(false)} title="Chiudi" data-testid="assistant-close" className="h-8 w-8 flex items-center justify-center rounded-sm hover:bg-white/15 transition-colors"><X size={16} /></button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-secondary/20" data-testid="assistant-messages">
            {messages.length === 0 && (
              <div className="text-center text-sm text-muted-foreground pt-6">
                <Sparkle size={28} weight="fill" className="mx-auto text-primary/40 mb-2" />
                <p className="font-medium text-foreground">Come posso aiutarti?</p>
                <p className="text-xs mt-1 mb-3">Domande generiche, bozze di risposte helpdesk, riassunti interventi.</p>
                <div className="space-y-1.5">
                  {SUGGESTS.map((s, i) => (
                    <button key={i} onClick={() => setInput(s)} className="block w-full text-left text-xs px-3 py-2 rounded-md bg-white border border-border hover:border-primary/40 hover:bg-primary/5 transition-colors">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`assistant-msg-${m.role}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words ${m.role === "user" ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-white border border-border rounded-bl-sm"}`}>
                  {m.content || (busy && i === messages.length - 1 ? <span className="inline-flex gap-1"><span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: "0ms" }} /><span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: "150ms" }} /><span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: "300ms" }} /></span> : "")}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-border p-2.5 bg-white">
            <div className="flex items-end gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Scrivi un messaggio…"
                rows={1}
                data-testid="assistant-input"
                className="flex-1 rounded-xl resize-none min-h-[40px] max-h-28 text-sm"
              />
              <Button onClick={send} disabled={busy || !input.trim()} data-testid="assistant-send" className="rounded-xl h-10 w-10 p-0 shrink-0">
                <PaperPlaneRight size={17} weight="fill" />
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1.5 text-center">L'AI può commettere errori. Verifica le informazioni importanti.</p>
          </div>
        </div>
      )}
    </>
  );
}
